/**
 * Refresh every session Home / Picks / Standings cache entry from the API.
 * Used after an unexpected AppSync drop so unmounted pages are not left stale.
 */

import api from './api';
import {
  applyLiveEventToRoundsCache,
  betsByLeagueStore,
  homeByLeagueStore,
  standingsByLeagueStore
} from '../utils/leaguePageCache';
import { collectGamesFromRoundsData, liveSnapshotFromGame } from '../utils/mergeLiveGame';

/**
 * @param {string} leagueCode
 * @param {{ get: (url: string) => Promise<{ data: unknown }> }} apiClient
 */
async function refreshHomeLeague(leagueCode, apiClient) {
  try {
    const response = await apiClient.get(`/rounds/${leagueCode}/current`);
    const round = response.data;
    const games = Array.isArray(round?.games) ? round.games : [];
    const previousGames = Array.isArray(round?.previousGames) ? round.previousGames : [];
    const prev = homeByLeagueStore.get();
    homeByLeagueStore.set({
      ...prev,
      [leagueCode]: {
        currentWeek: round,
        games,
        previousGames
      }
    });
  } catch (err) {
    if (err?.response?.status === 404) {
      const prev = homeByLeagueStore.get();
      homeByLeagueStore.set({
        ...prev,
        [leagueCode]: { currentWeek: null, games: [], previousGames: [] }
      });
      return;
    }
    console.warn(`refetchLivePageCaches home ${leagueCode} failed`, err);
  }
}

/**
 * Merge fresh live game fields into the existing picks cache (keeps local bet drafts).
 * @param {string} leagueCode
 * @param {{ get: (url: string) => Promise<{ data: unknown }> }} apiClient
 */
async function refreshBetsLeague(leagueCode, apiClient) {
  try {
    const response = await apiClient.get(`/mypicks/${leagueCode}`);
    const games = collectGamesFromRoundsData(response.data);
    let cache = betsByLeagueStore.get();
    if (!cache[leagueCode]) return;
    for (const game of games) {
      if (!game?.gameId) continue;
      cache = applyLiveEventToRoundsCache(cache, game.gameId, liveSnapshotFromGame(game));
    }
    betsByLeagueStore.set(cache);
  } catch (err) {
    if (err?.response?.status === 401) return;
    console.warn(`refetchLivePageCaches bets ${leagueCode} failed`, err);
  }
}

/**
 * @param {string} leagueCode
 * @param {{ get: (url: string) => Promise<{ data: unknown }> }} apiClient
 */
async function refreshStandingsLeague(leagueCode, apiClient) {
  try {
    const response = await apiClient.get(`/standings/${leagueCode}`);
    const prev = standingsByLeagueStore.get();
    standingsByLeagueStore.set({
      ...prev,
      [leagueCode]: response.data
    });
  } catch (err) {
    if (err?.response?.status === 401) return;
    console.warn(`refetchLivePageCaches standings ${leagueCode} failed`, err);
  }
}

/**
 * Refetch every league present in Home / Picks / Standings session caches.
 * @param {{ apiClient?: { get: (url: string) => Promise<{ data: unknown }> } }} [opts]
 */
export async function refetchAllLivePageCaches(opts = {}) {
  const apiClient = opts.apiClient || api;
  const homeCodes = Object.keys(homeByLeagueStore.get() || {});
  const betsCodes = Object.keys(betsByLeagueStore.get() || {});
  const standingsCodes = Object.keys(standingsByLeagueStore.get() || {});

  await Promise.all([
    ...homeCodes.map((code) => refreshHomeLeague(code, apiClient)),
    ...betsCodes.map((code) => refreshBetsLeague(code, apiClient)),
    ...standingsCodes.map((code) => refreshStandingsLeague(code, apiClient))
  ]);
}
